
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { eq } from 'lodash';

import { getTemplateStatus } from '../../../../../../core/api/external-sheets';

export interface InputDataItem {
  forecastId?: string;
  organisationId: string;
  workspaceId: string;
}

export interface SheetSyncStatus {
  allowImport: boolean;
  error?: string;
  sheetsToImport: string[];
  incorrectSheetsToImport: string[];
}

/**
 * Hook that will retrieve the master google sheet sync status for the given input items
 * @param inputForecastData - Array of input data. Please make sure this is a stable variable (use useState / useMemo if needed),
 *                           otherwise you will get into a loop that causes endless requests.
 */
export const useSheetsSyncStatus = (inputItems: InputDataItem[]) => {
  const [idsLoading, setIdsLoading] = useState<string[]>([]);
  const [results, setResults] = useState<Record<string, SheetSyncStatus>>({});
  const requestedData = useRef<Required<InputDataItem>[]>([]);

  const requestSheetData = useCallback(async (item: Required<InputDataItem>): Promise<SheetSyncStatus> => {
    try {
      const data = await getTemplateStatus(item.workspaceId, item.organisationId, item.forecastId);
      return {
        allowImport: !data.incorrect_sheets_to_import.length && data.sheets_to_import.length > 0,
        error: data.error || undefined,
        sheetsToImport: data.sheets_to_import,
        incorrectSheetsToImport: data.incorrect_sheets_to_import
      };
    } catch (e) {
      return {
        allowImport: false,
        error: 'Failed to load templates from import sheet',
        sheetsToImport: [],
        incorrectSheetsToImport: []
      };
    }
  }, []);

  const requestAllSheetsData = useCallback(
    async (items: Required<InputDataItem>[]) => {
      const output: Record<string, SheetSyncStatus> = {};
      requestedData.current = items;
      let remainingIds = items.map(item => item.forecastId);
      setIdsLoading(remainingIds);

      const load = async (item: Required<InputDataItem>) => {
        const data = await requestSheetData(item);
        if (items === requestedData.current) {
          output[item.forecastId] = data;
          setResults(output);
          remainingIds = remainingIds.filter(forecastId => forecastId !== item.forecastId);
          setIdsLoading(remainingIds);
        }
      };

      items.forEach(item => {
        load(item);
      });
    },
    [requestSheetData]
  );

  // Reload data when input parameters change
  useEffect(() => {
    if (inputItems.length) {
      const newInputItems = inputItems.filter(item => !!item.forecastId) as Required<InputDataItem>[];
      if (!eq(newInputItems, requestedData.current)) {
        requestAllSheetsData(newInputItems);
      }
    }
  }, [inputItems, requestAllSheetsData]);

  return { isLoading: idsLoading.length > 0, idsLoading, results };
};

/**
 * Hook that will retrieve the master google sheet sync status for the given forecast ID
 * @param workspaceId - Workspace ID
 * @param organisationId - Organisation ID
 * @param inputForecastId - Forecast ID
 */
export const useSheetSyncStatus = (workspaceId: string, organisationId: string, forecastId: string | undefined) => {
  const input = useMemo(() => [{ workspaceId, organisationId, forecastId }], [workspaceId, organisationId, forecastId]);
  const { isLoading, results } = useSheetsSyncStatus(input);
  return {
    isLoading,
    result: forecastId ? results[forecastId] : undefined
  };
};
